// The client-side plugin GRAPHICS VM (Set 12.5). A directory plugin may ship a `client.lua` that runs
// HERE, in the browser, in its own Fengari (pure-JS Lua) state — the presentation half of a plugin,
// mirroring the lua-animations harness on ericdube.com. It registers a draw function per sprite name
// with `sprite("name", function(e, t) … end)`, using the canvas primitives below; the renderer calls
// it for any entity whose sprite `sh` is `<plugin>.<name>`. It's pure presentation: it never touches
// the sim, so math.random and wall-clock `t` are free — no seed discipline. It sees only the drawing
// API + the stdlib (math/table/string); no DOM, no io. Base-game sprites stay JS — only plugin
// entities cross into Lua, so the per-frame Lua cost is paid only where a plugin actually draws.

// In Node (the headless test) this resolves to plain `fengari`; the client BUILD aliases `fengari` ->
// `fengari-web` (the browser distribution that stubs Node's fs/os/process itself), so the bundle isn't
// stuck stubbing Fengari's transitive Node deps. Robust default-vs-namespace access covers both.
import * as fengariNS from "fengari";
import { makeWidget, addChild } from "./widget-tree.js";
const fengari = fengariNS.default ?? fengariNS;
const { lua, lauxlib, lualib, to_luastring } = fengari;

// Push a JS value as a Lua value, DEEP (nested objects → tables, arrays → 1-based tables). Powers self()
// (the player's synced state) + the grid cells provider's return trip. A null/undefined array element
// becomes `false`, not nil, so an array with empty slots stays a contiguous sequence (nil would end it).
function pushDeep(L, v) {
    if (typeof v === "number") lua.lua_pushnumber(L, v);
    else if (typeof v === "string") lua.lua_pushstring(L, to_luastring(v));
    else if (typeof v === "boolean") lua.lua_pushboolean(L, v ? 1 : 0);
    else if (Array.isArray(v)) {
        lua.lua_newtable(L);
        for (let i = 0; i < v.length; i++) { const e = v[i]; if (e == null) lua.lua_pushboolean(L, 0); else pushDeep(L, e); lua.lua_rawseti(L, -2, i + 1); }
    } else if (v && typeof v === "object") {
        lua.lua_newtable(L);
        for (const k in v) { pushDeep(L, v[k]); lua.lua_setfield(L, -2, to_luastring(k)); }
    } else lua.lua_pushnil(L);
}

// Push a snapshot entity as a Lua table of its fields (x, y, and whatever else rode the wire).
// Nested tables cross too (Set 17): an entity's `display` bag (e.d) is how a plugin marker shows
// live state — a hill's holder, a point's team — to its own client.lua.
function pushEnt(L, e) {
    lua.lua_newtable(L);
    for (const k in e) {
        const v = e[k];
        if (typeof v === "number") lua.lua_pushnumber(L, v);
        else if (typeof v === "string") lua.lua_pushstring(L, to_luastring(v));
        else if (typeof v === "boolean") lua.lua_pushboolean(L, v ? 1 : 0);
        else if (v && typeof v === "object") pushDeep(L, v);
        else continue;
        lua.lua_setfield(L, -2, to_luastring(k));
    }
}

// Read a Lua value (scalars + nested tables) at a stack index into a JS value — for send()'s payload,
// which crosses back to the server. Contiguous 1..n integer keys become an array, like the host's reader.
function readValue(L, idx) {
    const t = lua.lua_type(L, idx);
    if (t === lua.LUA_TNUMBER) return lua.lua_tonumber(L, idx);
    if (t === lua.LUA_TSTRING) return lua.lua_tojsstring(L, idx);
    if (t === lua.LUA_TBOOLEAN) return !!lua.lua_toboolean(L, idx);
    if (t !== lua.LUA_TTABLE) return null;
    idx = lua.lua_absindex(L, idx);
    const obj = {}; let arrayish = true, count = 0, maxInt = 0;
    lua.lua_pushnil(L);
    while (lua.lua_next(L, idx) !== 0) {
        const kt = lua.lua_type(L, -2); let key;
        if (kt === lua.LUA_TNUMBER) { key = lua.lua_tonumber(L, -2); if (Number.isInteger(key) && key >= 1) maxInt = Math.max(maxInt, key); else arrayish = false; }
        else if (kt === lua.LUA_TSTRING) { key = lua.lua_tojsstring(L, -2); arrayish = false; }
        else { lua.lua_pop(L, 1); continue; }
        obj[key] = readValue(L, -1); count++; lua.lua_pop(L, 1);
    }
    if (arrayish && count === maxInt) { const a = []; for (let i = 1; i <= maxInt; i++) a.push(obj[i]); return a; }
    return obj;
}

// Build a VM from the enabled plugins' client scripts: [{ name, source }]. `images` maps a namespaced
// asset key "<plugin>.<name>" to a drawable (a loaded HTMLImageElement in the browser; a mock in tests)
// — a live reference the caller may keep mutating as assets finish loading. Returns { has, draw }.
export function createSpriteVM(scripts = [], images = {}) {
    const L = lauxlib.luaL_newstate();
    lualib.luaL_openlibs(L); // math (incl. random), string, table — everything a drawer needs

    let g = null;         // the current 2D context, set for the duration of one draw
    const holds = {};     // item name -> { ref, plugin } — how the item looks IN HAND (Set 14)
    const binds = [];     // client-declared bindables: { id, owner, spec } (Set 16)
    // A flat Lua table of scalars at `idx`, as a JS object — a bind()'s spec.
    const readSpec = (idx) => {
        const out = {};
        idx = lua.lua_absindex(L, idx);
        if (lua.lua_type(L, idx) !== lua.LUA_TTABLE) return out;
        lua.lua_pushnil(L);
        while (lua.lua_next(L, idx) !== 0) {
            if (lua.lua_type(L, -2) === lua.LUA_TSTRING) {
                const t = lua.lua_type(L, -1);
                const k = lua.lua_tojsstring(L, -2);
                if (t === lua.LUA_TSTRING) out[k] = lua.lua_tojsstring(L, -1);
                else if (t === lua.LUA_TNUMBER) out[k] = lua.lua_tonumber(L, -1);
                else if (t === lua.LUA_TBOOLEAN) out[k] = !!lua.lua_toboolean(L, -1);
            }
            lua.lua_pop(L, 1);
        }
        return out;
    };
    let loading = null;   // plugin whose client.lua is loading now — namespaces its sprites + input hooks
    let drawing = null;   // plugin whose sprite is drawing now — namespaces its image() lookups
    let running = null;   // plugin whose input handler is running now — namespaces its send()
    let onSend = null;    // (plugin, event, data) => void — wired to the network by the caller
    let onPicker = null;  // (options, resolve) => void — opens the client's ONE radial picker (Set 21.5); wired by the caller
    let onSelf = null;    // () => the local player's synced state (object) — wired by the caller for self()
    let onHold = null;    // (name) => void — hold an item (Set 12.11), driving selection; wired by the caller
    let onZoom = null;    // (factor) => void — set the camera's target zoom (Set 16); wired by the caller
    const inputs = {};    // action -> [{ plugin, ref }] — client.lua on_input handlers (Set 12.5 runtime)
    const widgets = new Map(); // id -> macro-widget node (Set 12.5): every widget client.lua creates
    const mounts = [];    // [{ widget, anchor }] — HUD-mounted widgets (ui.hud), always drawn
    const sprites = {};   // "<plugin>.<name>" -> registry ref of the Lua draw fn
    const icons = {};     // itemname -> { ref, plugin } — a plugin's own item ICON art (Set 12.5)
    const broken = new Set();
    const n = (i) => lua.lua_tonumber(L, i); // arg reader (0 if absent/non-number)

    // Bind a JS drawing op as a Lua global.
    const op = (name, fn) => { lua.lua_pushjsfunction(L, () => { if (g) fn(); return 0; }); lua.lua_setglobal(L, to_luastring(name)); };

    // --- colour + line state ---
    op("fill_rgb", () => { g.fillStyle = `rgba(${n(1)},${n(2)},${n(3)},${lua.lua_gettop(L) >= 4 ? n(4) : 1})`; });
    op("fill_hsl", () => { g.fillStyle = `hsla(${n(1)},${n(2)}%,${n(3)}%,${lua.lua_gettop(L) >= 4 ? n(4) : 1})`; });
    op("stroke_rgb", () => { g.strokeStyle = `rgba(${n(1)},${n(2)},${n(3)},${lua.lua_gettop(L) >= 4 ? n(4) : 1})`; });
    op("stroke_hsl", () => { g.strokeStyle = `hsla(${n(1)},${n(2)}%,${n(3)}%,${lua.lua_gettop(L) >= 4 ? n(4) : 1})`; });
    op("line_width", () => { g.lineWidth = n(1); });
    op("alpha", () => { g.globalAlpha = n(1); });

    // --- immediate shapes ---
    op("circle", () => { g.beginPath(); g.arc(n(1), n(2), Math.max(0, n(3)), 0, 2 * Math.PI); g.fill(); });
    op("ring", () => { g.beginPath(); g.arc(n(1), n(2), Math.max(0, n(3)), 0, 2 * Math.PI); g.stroke(); });
    op("ellipse", () => { g.beginPath(); g.ellipse(n(1), n(2), Math.max(0, n(3)), Math.max(0, n(4)), lua.lua_gettop(L) >= 5 ? n(5) : 0, 0, 2 * Math.PI); g.fill(); }); // x, y, rx, ry, [rotation]
    op("line", () => { g.beginPath(); g.moveTo(n(1), n(2)); g.lineTo(n(3), n(4)); g.stroke(); });
    op("rect", () => { g.fillRect(n(1), n(2), n(3), n(4)); });
    op("frame", () => { g.strokeRect(n(1), n(2), n(3), n(4)); });
    // text(str, x, y, [size]) — a centred label in the current fill colour (Set 17): a crown over a
    // hill, a damage number, a sign. Size in px, default 12.
    op("text", () => {
        const s = lua.lua_tojsstring(L, 1) || "";
        g.font = `${lua.lua_gettop(L) >= 4 ? n(4) : 12}px system-ui, sans-serif`;
        g.textAlign = "center"; g.textBaseline = "alphabetic";
        g.fillText(s, n(2), n(3));
    });

    // --- paths (build then fill/stroke) — the expressive core ---
    op("path", () => g.beginPath());
    op("move", () => g.moveTo(n(1), n(2)));
    op("lineto", () => g.lineTo(n(1), n(2)));
    op("curve", () => g.quadraticCurveTo(n(1), n(2), n(3), n(4)));       // one control point
    op("bezier", () => g.bezierCurveTo(n(1), n(2), n(3), n(4), n(5), n(6))); // two control points
    op("arc", () => g.arc(n(1), n(2), Math.max(0, n(3)), n(4), n(5)));
    op("close", () => g.closePath());
    op("fill", () => g.fill());
    op("stroke", () => g.stroke());

    // --- transforms ---
    op("push", () => g.save());
    op("pop", () => g.restore());
    op("translate", () => g.translate(n(1), n(2)));
    op("rotate", () => g.rotate(n(1)));
    op("scale", () => g.scale(n(1), n(2)));

    // --- a soft radial glow (fire, light, shields) — the practical face of gradients ---
    op("glow", () => {
        const x = n(1), y = n(2), r = Math.max(0.01, n(3)), h = n(4), s = n(5), l = n(6);
        const grad = g.createRadialGradient(x, y, 0, x, y, r);
        grad.addColorStop(0, `hsla(${h},${s}%,${l}%,0.9)`);
        grad.addColorStop(1, `hsla(${h},${s}%,${l}%,0)`);
        g.fillStyle = grad; g.beginPath(); g.arc(x, y, r, 0, 2 * Math.PI); g.fill();
    });

    // image(name, x, y, w, h) — draw a raster the plugin shipped, resolved to <this-plugin>.<name> so a
    // plugin only ever reaches its own assets. w/h optional (natural size if omitted). A not-yet-loaded
    // or unknown image is a silent no-op — the sprite keeps drawing, the picture pops in once it arrives.
    lua.lua_pushjsfunction(L, () => {
        if (!g) return 0;
        const img = images[`${drawing}.${lua.lua_tojsstring(L, 1)}`];
        if (img) {
            if (lua.lua_gettop(L) >= 5) g.drawImage(img, n(2), n(3), n(4), n(5));
            else g.drawImage(img, n(2), n(3));
        }
        return 0;
    });
    lua.lua_setglobal(L, to_luastring("image"));

    // on_input(action, fn) — register a client-side handler for a bound input action (Set 12.5 runtime),
    // namespaced to the loading plugin. The handler runs on the CLIENT when that action fires; it may
    // hold local state and call send() to reach its own server half.
    lua.lua_pushjsfunction(L, () => {
        const action = lua.lua_tojsstring(L, 1);
        lua.lua_pushvalue(L, 2);
        const ref = lauxlib.luaL_ref(L, lua.LUA_REGISTRYINDEX);
        (inputs[action] || (inputs[action] = [])).push({ plugin: loading, ref });
        return 0;
    });
    lua.lua_setglobal(L, to_luastring("on_input"));

    // send(event, data) — from inside an input handler, message THIS plugin's server half. Routes through
    // onSend(plugin, event, data); the server delivers it plugin-private to plugin.on_client(event).
    lua.lua_pushjsfunction(L, () => {
        if (onSend && running) onSend(running, lua.lua_tojsstring(L, 1), lua.lua_gettop(L) >= 2 ? readValue(L, 2) : null);
        return 0;
    });
    lua.lua_setglobal(L, to_luastring("send"));

    // --- Macro-widgets (Set 12.5): the plugin UI surface. client.lua composes engine-drawn widgets —
    // ui.modal / vstack / hstack / icon_grid / hud — and the renderer walks the tree + InputService
    // hit-tests it (both via widget-tree.js). A Lua HANDLE is a table carrying { _id } + its methods; the
    // JS widget nodes live in `widgets`. See the "macro-widgets" artifact. ---

    // Recover the widget node from a handle table at stack index `idx` (reads its _id).
    const widgetAt = (idx) => {
        const ai = lua.lua_absindex(L, idx);
        lua.lua_getfield(L, ai, to_luastring("_id"));
        const w = widgets.get(lua.lua_tonumber(L, -1)); lua.lua_pop(L, 1);
        return w || null;
    };
    // Call a widget's Lua callback (grid on_click/on_move, or the cells provider) under the right plugin
    // namespace, with JS args pushed deep. `nres` results are read back (cells wants 1; events want 0).
    const callWidgetCb = (plugin, ref, args, nres) => {
        const prev = running; running = plugin;
        lua.lua_rawgeti(L, lua.LUA_REGISTRYINDEX, ref);
        for (const a of args) pushDeep(L, a);
        let out = null;
        if (lua.lua_pcall(L, args.length, nres, 0) !== lua.LUA_OK) { console.error(`[client.lua "${plugin}"] widget cb:`, lua.lua_tojsstring(L, -1)); lua.lua_pop(L, 1); }
        else if (nres) { out = readValue(L, -1); lua.lua_pop(L, nres); }
        running = prev;
        return out;
    };
    // Push a fresh handle table for widget `w` with the named methods (each a shared jsfunction that
    // recovers its widget from arg 1's _id). Leaves the handle on the stack (returned to Lua).
    const pushHandle = (w, methods) => {
        lua.lua_newtable(L);
        lua.lua_pushnumber(L, w.id); lua.lua_setfield(L, -2, to_luastring("_id"));
        for (const name in methods) { lua.lua_pushjsfunction(L, methods[name]); lua.lua_setfield(L, -2, to_luastring(name)); }
    };

    // container method: add(self, child) — nest a widget, return self (so calls can chain)
    const m_add = () => { const p = widgetAt(1), c = widgetAt(2); if (p && c) addChild(p, c); lua.lua_pushvalue(L, 1); return 1; };
    // modal lifecycle
    const m_show = () => { const w = widgetAt(1); if (w) w.open = true; return 0; };
    const m_hide = () => { const w = widgetAt(1); if (w) w.open = false; return 0; };
    const m_toggle = () => { const w = widgetAt(1); if (w) w.open = !w.open; return 0; };
    const m_isopen = () => { const w = widgetAt(1); lua.lua_pushboolean(L, w && w.open ? 1 : 0); return 1; };
    const MODAL_METHODS = { add: m_add, show: m_show, hide: m_hide, toggle: m_toggle, is_open: m_isopen };
    const STACK_METHODS = { add: m_add };

    // Capture a config function field (cfg.<name>, the config table being at stack index 1) as a registry
    // ref bound to the loading plugin. Returns a JS caller (args, nres) => result, or null if not a fn.
    const grabFn = (name) => {
        lua.lua_getfield(L, 1, to_luastring(name));
        if (!lua.lua_isfunction(L, -1)) { lua.lua_pop(L, 1); return null; }
        const ref = lauxlib.luaL_ref(L, lua.LUA_REGISTRYINDEX), plugin = loading || running; // pops the fn
        return (args, nres) => callWidgetCb(plugin, ref, args, nres);
    };

    // A layout container creator (vstack / hstack). Optional children array as arg 1: ui.vstack({a, b}).
    const makeStack = (type) => () => {
        const w = makeWidget(type, {}); widgets.set(w.id, w);
        if (lua.lua_istable(L, 1)) {
            const nch = lua.lua_rawlen(L, 1);
            for (let i = 1; i <= nch; i++) { lua.lua_rawgeti(L, 1, i); const c = widgetAt(-1); if (c) addChild(w, c); lua.lua_pop(L, 1); }
        }
        pushHandle(w, STACK_METHODS); return 1;
    };

    lua.lua_newtable(L);
    // ui.modal({ title }) -> handle
    lua.lua_pushjsfunction(L, () => { const w = makeWidget("modal", readValue(L, 1) || {}); widgets.set(w.id, w); pushHandle(w, MODAL_METHODS); return 1; });
    lua.lua_setfield(L, -2, to_luastring("modal"));
    lua.lua_pushjsfunction(L, makeStack("vstack")); lua.lua_setfield(L, -2, to_luastring("vstack"));
    lua.lua_pushjsfunction(L, makeStack("hstack")); lua.lua_setfield(L, -2, to_luastring("hstack"));
    // ui.grid({ cols, rows, gap, cells, cell, on_click, on_move }) -> handle. The GENERIC grid (Set 12.5,
    // step 2): the engine owns the layout + the repeat + the rebuild-when-`cells`-changes; `cell(item, i)`
    // is a plugin template returning that cell's CONTENT (ui.icon / ui.label descriptors), so icon_grid
    // itself lives in a kit plugin, composed from these primitives. on_click/on_move are cell-indexed at
    // the grid level (no per-cell closures → no churn). cells/cell/on_click are captured once here.
    lua.lua_pushjsfunction(L, () => {
        const cfg = readValue(L, 1) || {}; // cols/rows/gap (the fn fields read back as null — captured below)
        const w = makeWidget("grid", { cols: cfg.cols, rows: cfg.rows, gap: cfg.gap }); widgets.set(w.id, w);
        const cells = grabFn("cells"); if (cells) w.cells = () => { const o = cells([], 1); return Array.isArray(o) ? o : []; };
        const cell = grabFn("cell"); if (cell) w.cellTemplate = (item, i) => { const o = cell([item, i], 1); return Array.isArray(o) ? o : o ? [o] : []; };
        // Optional: selected() -> the item NAME to highlight (the held item). The engine marks the cell(s)
        // holding it, so a grid can show your selection the way the carousel centres it (Set 12.9).
        const selected = grabFn("selected"); if (selected) w.selected = () => selected([], 1);
        const onClick = grabFn("on_click"); if (onClick) w.onClick = (i, item) => onClick([i, item], 0);
        const onMove = grabFn("on_move"); if (onMove) w.onMove = (a, b) => onMove([a, b], 0);
        // Cross-grid drag (Set 20.2): `id` tags a grid as a drag participant, and on_drop(srcId, from, to,
        // item) fires when an item DRAGGED FROM ANOTHER grid is released on this one — the counterpart of
        // on_move (same-grid rearrange). Lets one grid (a crafting bench) accept items dragged from another
        // (your inventory) and vice-versa, staged entirely client-side.
        w.dropId = typeof cfg.id === "string" ? cfg.id : null;
        const onDrop = grabFn("on_drop"); if (onDrop) w.onDrop = (srcId, from, to, item) => onDrop([srcId, from, to, item], 0);
        pushHandle(w, {}); return 1; // just an _id handle (nested via a stack's children array / modal:add)
    });
    lua.lua_setfield(L, -2, to_luastring("grid"));
    // ui.carousel({ state }) -> handle. A fat engine widget (Set 12.9): an animated horizontal strip of
    // item chips with the SELECTED one slid to dead-centre and a cooldown sweep on it — the shooter hotbar,
    // now a PLUGIN (weapons are items). `state()` is read fresh each frame and returns
    //   { items = [{ item, count, infinite, swatch, label }], selected = <index>, ready = 0..1 (1 = off cd) }.
    // The engine owns the animated render (widget-dom.js: CSS-transform slide + the sweep); the plugin only
    // points it at the inventory's weapons. Like ui.grid, the strip rebuilds only when its items change.
    lua.lua_pushjsfunction(L, () => {
        const w = makeWidget("carousel", {}); widgets.set(w.id, w);
        const state = grabFn("state"); if (state) w.carouselState = () => state([], 1);
        pushHandle(w, {}); return 1; // just an _id handle (mounted via ui.hud)
    });
    lua.lua_setfield(L, -2, to_luastring("carousel"));
    // ui.wheel({ options, on_pick }) — open the ONE composable radial picker (Set 21.5). `options` is a list
    // (or a fn returning one) of { value, label?, count?, icon?, tint?, color? }; the engine draws the radial
    // and, on commit, calls on_pick(value) with the chosen option's value (or nil if cancelled) on the
    // client — the plugin decides what to send(). This is imperative (opened from an on_input), like the old
    // wheel.show, but takes ARBITRARY options: the item wheel, the trowel block wheel, and menus fold onto it.
    lua.lua_pushjsfunction(L, () => {
        const cfg = readValue(L, 1) || {};
        const optsFn = grabFn("options"), onPick = grabFn("on_pick");
        if (!onPicker) return 0;
        const raw = optsFn ? optsFn([], 1) : cfg.options;
        const options = (Array.isArray(raw) ? raw : []).map((o) => ({
            value: o.value, label: o.label, count: o.count, icon: o.icon, tint: o.tint, color: o.color,
        }));
        onPicker(options, (picked) => { if (onPick) onPick([picked == null ? null : picked], 0); });
        return 0;
    });
    lua.lua_setfield(L, -2, to_luastring("wheel"));
    // ui.text(str) -> handle. A static text WIDGET — a section heading or a slot label, the one non-cell
    // way to put words in a modal. Nested via a stack's children array like a grid ({ ui.text("Pot"), … }).
    lua.lua_pushjsfunction(L, () => { const w = makeWidget("text", { text: lua.lua_tojsstring(L, 1) || "" }); widgets.set(w.id, w); pushHandle(w, {}); return 1; });
    lua.lua_setfield(L, -2, to_luastring("text"));
    // ui.icon(item) / ui.label(text) — CELL-CONTENT descriptors (plain tables, not widgets): a cell
    // template returns a list of these and the engine renders them (an item's icon; a text span). They're
    // ref-less + node-less, so a grid rebuild creates no garbage in the widget map or the Lua registry.
    lua.lua_pushjsfunction(L, () => { lua.lua_newtable(L); lua.lua_pushstring(L, to_luastring("icon")); lua.lua_setfield(L, -2, to_luastring("kind")); lua.lua_pushvalue(L, 1); lua.lua_setfield(L, -2, to_luastring("item")); return 1; });
    lua.lua_setfield(L, -2, to_luastring("icon"));
    lua.lua_pushjsfunction(L, () => { lua.lua_newtable(L); lua.lua_pushstring(L, to_luastring("label")); lua.lua_setfield(L, -2, to_luastring("kind")); lua.lua_pushvalue(L, 1); lua.lua_setfield(L, -2, to_luastring("text")); return 1; });
    lua.lua_setfield(L, -2, to_luastring("label"));
    // ui.tip(text) — what the cell says when you point at it (Set 17.1, Eric: an inventory cell should
    // name the thing in it). A descriptor like the two above rather than an engine rule, because WHAT to
    // say about an item is the plugin's business the same way the icon and the count already are: the
    // ammo cells could read "12 / 12" tomorrow without the engine learning what ammo is.
    lua.lua_pushjsfunction(L, () => { lua.lua_newtable(L); lua.lua_pushstring(L, to_luastring("tip")); lua.lua_setfield(L, -2, to_luastring("kind")); lua.lua_pushvalue(L, 1); lua.lua_setfield(L, -2, to_luastring("text")); return 1; });
    lua.lua_setfield(L, -2, to_luastring("tip"));
    // ui.bar(frac, color) — a USAGE BAR overlay on a cell (Set 19): a thin fill along the cell's
    // bottom edge showing frac (0..1) of some depleting quantity — an item's remaining uses before it
    // breaks. A descriptor like icon/label/tip, so any item representation can carry a bar without the
    // engine knowing what "uses" means; the wear rule stays the plugin's. `color` tints the fill (a
    // green→red gradient the caller picks); omit it for the default.
    lua.lua_pushjsfunction(L, () => { lua.lua_newtable(L); lua.lua_pushstring(L, to_luastring("bar")); lua.lua_setfield(L, -2, to_luastring("kind")); lua.lua_pushvalue(L, 1); lua.lua_setfield(L, -2, to_luastring("frac")); lua.lua_pushvalue(L, 2); lua.lua_setfield(L, -2, to_luastring("color")); return 1; });
    lua.lua_setfield(L, -2, to_luastring("bar"));
    // ui.swatch(item) — fill a cell with a solid colour block, taken from that item's manifest tint
    // (Set 19, the block maker's pixel editor + palette). A descriptor like the others; the colour comes
    // from the item registry so the client needs no palette of its own. An item with no tint reads empty.
    lua.lua_pushjsfunction(L, () => { lua.lua_newtable(L); lua.lua_pushstring(L, to_luastring("swatch")); lua.lua_setfield(L, -2, to_luastring("kind")); lua.lua_pushvalue(L, 1); lua.lua_setfield(L, -2, to_luastring("item")); return 1; });
    lua.lua_setfield(L, -2, to_luastring("swatch"));
    // ui.hud(widget, anchor) — mount a widget on the HUD at a screen anchor ("bottom", "top-left", …)
    lua.lua_pushjsfunction(L, () => { const w = widgetAt(1); const anchor = lua.lua_isstring(L, 2) ? lua.lua_tojsstring(L, 2) : "top-left"; if (w) mounts.push({ widget: w, anchor }); lua.lua_pushvalue(L, 1); return 1; });
    lua.lua_setfield(L, -2, to_luastring("hud"));
    lua.lua_setglobal(L, to_luastring("ui"));

    // self() — the local player's synced state (hp, pos, inventory.slots/hotbar, …) as a Lua table, so a
    // grid's cells provider can read `self().inventory.slots`. Read-only snapshot view; wired by the caller.
    lua.lua_pushjsfunction(L, () => { pushDeep(L, onSelf ? onSelf() : null); return 1; });
    lua.lua_setglobal(L, to_luastring("self"));

    // hold(name) — a hotbar plugin holds the named item (Set 12.11): pure selection (drives the highlight +
    // the list a plugin cycles). Firing is separate; the engine derives the fire weapon from the held item.
    // The plugin owns ALL the selection LOGIC (cycling, number keys). No-op until wired (e.g. headless tests).
    lua.lua_pushjsfunction(L, () => {
        if (onHold && lua.lua_isstring(L, 1)) onHold(lua.lua_tojsstring(L, 1));
        return 0;
    });
    lua.lua_setglobal(L, to_luastring("hold"));

    // set_zoom(f) — pull the camera back to `f` times the resting view (1 = normal). The engine owns
    // the smoothing; a plugin only ever says where it wants to end up (Set 16). This is what lets the
    // zoom BEHAVIOUR be a plugin at all — hold-a-key and step-with-the-wheel are two different games,
    // and neither belongs in the renderer.
    lua.lua_pushjsfunction(L, () => { if (onZoom) onZoom(lua.lua_tonumber(L, 1)); return 0; });
    lua.lua_setglobal(L, to_luastring("set_zoom"));

    // bind(id, spec) — declare a CLIENT-side bindable: a named action with a default key, a label for
    // the keybind menu, and optional `mods`/`hold` (Set 16). A server plugin declares controls in its
    // main.lua and they ride `welcome`; a client-ONLY plugin (a `.client.lua`) has no server half to
    // declare anything, so it says so here. The handler is the usual on_input(id, fn).
    //   mods = "shift"  only fires while shift is held (and the un-modified key still does its own job)
    //   hold = true     fires on press AND release, with { down = 1|0 }
    lua.lua_pushjsfunction(L, () => {
        const id = `${loading}.${lua.lua_tojsstring(L, 1)}`;
        binds.push({ id, owner: loading, spec: readSpec(2) });
        return 0;
    });
    lua.lua_setglobal(L, to_luastring("bind"));

    // sprite(name, fn) — register a draw function, namespaced to the loading plugin.
    lua.lua_pushjsfunction(L, () => {
        const key = `${loading}.${lua.lua_tojsstring(L, 1)}`;
        lua.lua_pushvalue(L, 2);
        sprites[key] = lauxlib.luaL_ref(L, lua.LUA_REGISTRYINDEX);
        return 0;
    });
    lua.lua_setglobal(L, to_luastring("sprite"));

    // icon(itemname, fn) — define how an ITEM's icon is drawn: the plugin's own vector art around the
    // origin (like the base pickupIcon shapes, but plugin-OWNED), using the same drawing primitives as
    // sprites. Keyed by item name; the engine draws it wherever the item's icon appears (the inventory,
    // a ground pickup). An item with no icon() falls back to the built-in shape for its manifest key.
    lua.lua_pushjsfunction(L, () => {
        lua.lua_pushvalue(L, 2);
        icons[lua.lua_tojsstring(L, 1)] = { ref: lauxlib.luaL_ref(L, lua.LUA_REGISTRYINDEX), plugin: loading };
        return 0;
    });
    lua.lua_setglobal(L, to_luastring("icon"));

    // hold(itemname, fn) — define how an item looks IN THE HAND (Set 14), drawn in the weapon frame:
    // hand at the origin, +x down the aim, already mirrored upright when aiming left. This is the
    // "separate item vs hold graphics" half; an item that defines no hold() is drawn from its ICON
    // instead, so holding something looks like something without a plugin doing any work.
    lua.lua_pushjsfunction(L, () => {
        lua.lua_pushvalue(L, 2);
        holds[lua.lua_tojsstring(L, 1)] = { ref: lauxlib.luaL_ref(L, lua.LUA_REGISTRYINDEX), plugin: loading };
        return 0;
    });
    lua.lua_setglobal(L, to_luastring("hold_art"));

    // Load each plugin's client.lua (a load-time error just skips that plugin's sprites).
    for (const s of scripts) {
        loading = s.name;
        if (lauxlib.luaL_dostring(L, to_luastring(s.source)) !== lua.LUA_OK) {
            console.error(`[client.lua "${s.name}"] load failed:`, lua.lua_tojsstring(L, -1)); lua.lua_pop(L, 1);
        }
    }
    loading = null;

    return {
        has: (sh) => sh in sprites && !broken.has(sh),
        // Does a plugin define a custom icon for this item? + draw it onto `ctx` (already set up centred/
        // scaled by the caller, so the fn draws around the origin like pickupIcon). Returns false if none.
        hasIcon: (name) => name in icons,
        setZoom: (fn) => { onZoom = fn; },
        binds: () => binds.slice(),   // client-declared bindables (Set 16), for the keybind menu

        // The same pair for HELD art (Set 14). `r` is the wielder's body radius, so the art scales with
        // the figure the way weapon art does.
        hasHold: (name) => name in holds,
        drawHold: (name, ctx, r, ink) => {
            const it = holds[name]; if (!it) return false;
            g = ctx; drawing = it.plugin;
            g.fillStyle = ink || "#cdd8f5"; g.strokeStyle = ink || "#cdd8f5"; g.lineWidth = 1.6; g.lineJoin = "round";
            const top = lua.lua_gettop(L);
            lua.lua_rawgeti(L, lua.LUA_REGISTRYINDEX, it.ref);
            lua.lua_pushnumber(L, r);
            if (lua.lua_pcall(L, 1, 0, 0) !== lua.LUA_OK) { console.error(`[client.lua "${it.plugin}"] hold "${name}":`, lua.lua_tojsstring(L, -1)); lua.lua_pop(L, 1); }
            lua.lua_settop(L, top); g = null; drawing = null;
            return true;
        },
        drawIcon: (name, ctx, tint) => {
            const it = icons[name]; if (!it) return false;
            g = ctx; drawing = it.plugin;
            // Pre-set the item's tint as the default fill/stroke (like pickupIcon), so a simple icon just
            // draws shapes in the tint; a multi-colour one overrides per part with fill_rgb/stroke_rgb.
            g.fillStyle = tint || "#cdd8f5"; g.strokeStyle = tint || "#cdd8f5"; g.lineWidth = 1.6; g.lineJoin = "round";
            const top = lua.lua_gettop(L);
            lua.lua_rawgeti(L, lua.LUA_REGISTRYINDEX, it.ref);
            if (lua.lua_pcall(L, 0, 0, 0) !== lua.LUA_OK) { console.error(`[client.lua "${it.plugin}"] icon "${name}":`, lua.lua_tojsstring(L, -1)); lua.lua_pop(L, 1); }
            lua.lua_settop(L, top); g = null;
            return true;
        },
        // --- client-plugin runtime (Set 12.5) ---
        setSend: (fn) => { onSend = fn; },       // wire send() to the network
        setPicker: (fn) => { onPicker = fn; },   // wire ui.wheel() to the client's ONE radial picker (Set 21.5)
        setSelf: (fn) => { onSelf = fn; },       // wire self() to the local player's synced state
        setHold: (fn) => { onHold = fn; },       // wire hold() to the engine's held-item primitive (Set 12.11)
        // --- macro-widgets (Set 12.5): the composed tree, for the renderer + InputService ---
        openModal: () => { for (const w of widgets.values()) if (w.type === "modal" && w.open) return w; return null; },
        hudMounts: () => mounts,                 // [{ widget, anchor }] — always-drawn HUD widgets
        hasInput: (action) => !!(inputs[action] && inputs[action].length), // does any plugin handle it?
        // Fire a bound input action → its plugins' on_input handlers. `arg` (e.g. the aim {x,y}) is passed
        // as a table; a handler may hold local state and call send(). A throwing handler is logged, not fatal.
        fireInput: (action, arg) => {
            const hs = inputs[action]; if (!hs || !hs.length) return;
            for (const h of hs) {
                running = h.plugin;
                const top = lua.lua_gettop(L);
                lua.lua_rawgeti(L, lua.LUA_REGISTRYINDEX, h.ref);
                pushEnt(L, arg || {});
                if (lua.lua_pcall(L, 1, 0, 0) !== lua.LUA_OK) { console.error(`[client.lua "${h.plugin}"] on_input "${action}":`, lua.lua_tojsstring(L, -1)); lua.lua_pop(L, 1); }
                lua.lua_settop(L, top);
                running = null;
            }
        },
        // Draw sprite `sh` for entity `e` at time `t` (seconds) onto context `ctx` (already in world
        // space under the camera, so the plugin draws at e.x / e.y like the JS sprites do).
        draw: (sh, ctx, e, t) => {
            const ref = sprites[sh];
            if (ref == null || broken.has(sh)) return;
            g = ctx;
            drawing = sh.slice(0, sh.indexOf(".")); // the plugin owning this sprite, for image() lookups
            const top = lua.lua_gettop(L);
            lua.lua_rawgeti(L, lua.LUA_REGISTRYINDEX, ref);
            pushEnt(L, e);
            lua.lua_pushnumber(L, t);
            if (lua.lua_pcall(L, 2, 0, 0) !== lua.LUA_OK) {
                console.error(`[client.lua] draw "${sh}" errored, disabling:`, lua.lua_tojsstring(L, -1));
                broken.add(sh); // a throwing sprite is dropped, not re-run every frame
            }
            lua.lua_settop(L, top); // leave the stack as we found it, whatever happened
            g = null;
            drawing = null;
        },
    };
}
